import { API_METHODS } from '@compass/client/constants/api-methods';
import type { OrganizationUser } from '@compass/client/types/organization_user';
import { MoreVertical, Plus, Search, Trash2, User as UserIcon, UserCog } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { RoleBadge } from '@/components/RoleBadge';
import { AddProgramMembersDialog } from '@/components/settings/AddProgramMembersDialog';
import { ChangeProgramRoleDialog } from '@/components/settings/ChangeProgramRoleDialog';
import { RemoveProgramMemberDialog } from '@/components/settings/RemoveProgramMemberDialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { UserAvatar } from '@/components/UserAvatar';
import { useProgramUsers } from '@/hooks/useProgramUsers';
import { useStore } from '@/store';

import { PermissionGuard } from '../PermissionGuard';
import { ProgramTeams } from './ProgramTeams';

export const ProgramMembers: React.FC = () => {
  const { selectedOrganizationId, selectedProgramId } = useStore();
  const [addMembersDialogOpen, setAddMembersDialogOpen] = useState(false);
  const [changeRoleUser, setChangeRoleUser] = useState<OrganizationUser | null>(null);
  const [removeMemberUser, setRemoveMemberUser] = useState<OrganizationUser | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading, error } = useProgramUsers(
    selectedOrganizationId ?? undefined,
    selectedProgramId ?? undefined,
    {
      limit: 100,
    },
  );

  const users = useMemo(() => {
    const allUsers = data?.users || [];
    if (!searchQuery.trim()) {
      return allUsers;
    }
    const query = searchQuery.toLowerCase().trim();
    return allUsers.filter(user => user.email?.toLowerCase().includes(query));
  }, [data?.users, searchQuery]);

  if (!selectedOrganizationId || !selectedProgramId) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <div className="py-8 text-center text-muted-foreground">
          Please select an organization and program to view members.
        </div>
      </div>
    );
  }

  const hasError = !!error;
  const allUsersCount = data?.users?.length || 0;
  const isEmpty = !isLoading && users.length === 0;
  const isSearchEmpty = isEmpty && searchQuery.trim() && allUsersCount > 0;

  return (
    <>
      <div className="rounded-lg border bg-card p-6">
        {/* Header - always visible */}
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">Members</h2>
            <p className="text-sm text-muted-foreground">
              Manage members and their roles within this program.
            </p>
          </div>
          <PermissionGuard apiMethod={[API_METHODS.ProgramUserRoleService.AddProgramUserRole]}>
            <Button onClick={() => setAddMembersDialogOpen(true)} size="sm">
              <Plus className="mr-2 h-4 w-4" />
              Add Members
            </Button>
          </PermissionGuard>
        </div>

        {/* Search input - full width */}
        <div className="relative mb-6">
          <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by email..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9"
          />
        </div>

        {/* Content - conditional rendering */}
        {hasError ? (
          <div className="py-8 text-center">
            <p className="text-sm text-destructive">Failed to load program members</p>
            <p className="mt-2 text-xs text-muted-foreground">{(error as Error).message}</p>
          </div>
        ) : isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-64" />
                </div>
              </div>
            ))}
          </div>
        ) : isEmpty ? (
          <div className="py-8 text-center text-muted-foreground">
            <UserIcon className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <p className="mt-4">
              {isSearchEmpty
                ? `No members found matching "${searchQuery}"`
                : 'No members found in this program'}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-md border">
            <div className="max-h-[340px] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 z-10 border-b bg-card">
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="sticky right-0 z-20 w-[50px] bg-card" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map(user => (
                    <TableRow key={user.user_id} className="group">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <UserAvatar
                            name={user.name || undefined}
                            email={user.email}
                            userId={user.user_id}
                            size="md"
                          />
                          <span className="font-medium">{user.name || 'Unnamed User'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <span>{user.email}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">{user.title || '—'}</span>
                      </TableCell>
                      <TableCell>
                        {user.roles && user.roles.length > 0 ? (
                          <RoleBadge role={user.roles[0]} size="sm" />
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="sticky right-0 z-10 bg-card group-hover:bg-muted">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setChangeRoleUser(user)}>
                              <UserCog className="mr-2 h-4 w-4" />
                              Change Role
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => setRemoveMemberUser(user)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Remove Member
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Dialogs */}
        {selectedOrganizationId && selectedProgramId && (
          <>
            <PermissionGuard apiMethod={[API_METHODS.ProgramUserRoleService.AddProgramUserRole]}>
              <AddProgramMembersDialog
                organizationId={selectedOrganizationId}
                programId={selectedProgramId}
                open={addMembersDialogOpen}
                onOpenChange={setAddMembersDialogOpen}
              />
            </PermissionGuard>
            <ChangeProgramRoleDialog
              organizationId={selectedOrganizationId}
              programId={selectedProgramId}
              user={changeRoleUser}
              open={!!changeRoleUser}
              onOpenChange={open => !open && setChangeRoleUser(null)}
            />
            <RemoveProgramMemberDialog
              organizationId={selectedOrganizationId}
              programId={selectedProgramId}
              user={removeMemberUser}
              open={!!removeMemberUser}
              onOpenChange={open => !open && setRemoveMemberUser(null)}
            />
          </>
        )}
      </div>

      {/* Program Teams Card */}
      {selectedOrganizationId && selectedProgramId && (
        <div className="mt-6">
          <ProgramTeams />
        </div>
      )}
    </>
  );
};
